/**
 * EntityTypePanel
 * ---------------
 * 
 * June 2026
 */

import DistributionSelection from './distributions/DistributionSelection.js';

import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';

const entityTypeWarnings = {
    'name': function(newName) { return newName === "" ? "Name is required" : null},
    'capacity': function(newCapacity) { return newCapacity <= 0 ? "Capacity must be positive" : null}
}

/**
 * Configuration sub-panel for editing an entity type's attributes.
 * 
 * @param {Object} config               - Configuration object for the simulation model. 
 * @param {Function} setConfig          - Setter for config object.
 * @param {Object} entityType           - Attributes for entity type being edited.
 * @param {Object} configWarnings       - Verification warnings for entity types.
 * @param {Function} setConfigWarnings  - Setter for verification warnings.
 * 
 * @todo Refactor verification handling for attributes applying distribution components.
 */
function EntityTypePanel({ config, setConfig, entityType, configWarnings, setConfigWarnings }) {
    if (!entityType) return null;

    /**
     * Updates warnings for an entity type's parameters.
     * 
     * @param {String} gid          - Entity type's identifier.
     * @param {String} parameter    - Element parameter linked to warning.
     * @param {String} warning      - Updated warning message.
     */
    const updateEntityTypeWarnings = (gid, parameter, warning) => {
        setConfigWarnings(prev => {
            const newWarnings = {...prev, entity_types: {...prev.entity_types, [gid]: {...prev.entity_types[gid]}}};
            if (warning) {
                newWarnings.entity_types[gid][parameter] = warning;
            } else {
                delete newWarnings.entity_types[gid][parameter];
            }
            return newWarnings;
        });
    }
    
    /**
     * Updates the entity type's name.
     * 
     * @param {String} newName  - Updated name.
     * @param {String} gid      - Entity type's identifier.
     */
    const handleEntityName = (newName, gid) => {
        updateEntityTypeWarnings(gid, 'name', entityTypeWarnings['name'](newName));
        
        const newEntityType = {...entityType, name: newName};
        const newEntityTypes = {...config.entity_types, [gid]: newEntityType};
        const newConfig = {...config, entity_types: newEntityTypes};
        setConfig(newConfig);
    }

    /**
     * Toggles the use of the entity type's capacity.
     * 
     * @param {Boolean} checked - Flag for enabling/disabling the capacity.
     * @param {String} gid      - Entity type's identifier.
     */
    const handleCapacityState = (checked, gid) => {
        const newCapacity = {...entityType.capacity, is_enabled: checked};
        const newEntityType = {...entityType, capacity: newCapacity};
        const newEntityTypes = {...config.entity_types, [gid]: newEntityType};
        const newConfig = {...config, entity_types: newEntityTypes};
        setConfig(newConfig);

        if (checked) {
            updateEntityTypeWarnings(gid, 'capacity', entityTypeWarnings['capacity'](newCapacity.value));
        } else {
            updateEntityTypeWarnings(gid, 'capacity', null);
        }
    }

    /**
     * Updates the capacity for arriving entities.
     * 
     * @param {Number} newValue - The updated capacity.
     * @param {String} gid      - Entity type's identifier.
     */
    const handleCapacity = (newValue, gid) => {
        updateEntityTypeWarnings(gid, 'capacity', entityTypeWarnings['capacity'](newValue));

        const newCapacity = {...entityType.capacity, value: newValue};
        const newEntityType = {...entityType, capacity: newCapacity};
        const newEntityTypes = {...config.entity_types, [gid]: newEntityType};
        const newConfig = {...config, entity_types: newEntityTypes};
        setConfig(newConfig);
    }

    /**
     * Updates entity type's attribute with a probability distribution.
     * 
     * Example attributes:
     *  - entities_per_arrival
     *  - interarrival
     * 
     * @param {String} attr     - Attribute key of the entity type.
     * @param {Object} newDist  - Updated distribution parameters.
     */
    const updateEntityDistribution = (attr, newDist) => {
        const newEntityType = {...entityType, [attr]: newDist};
        const newEntityTypes = {...config.entity_types, [entityType.gid]: newEntityType};
        const newConfig = {...config, 'entity_types': newEntityTypes};
        setConfig(newConfig);
    }

    return (
        <div style={{ marginBottom: '1rem' }}>
            <div style={{ marginBottom: '1rem'}}>
                <label style={{ marginRight: '1rem'}}>Name:</label>
                <InputText value={entityType.name} onChange={(event) => handleEntityName(event.target.value, entityType.gid)} style={{ width: '150px' }} invalid={!!configWarnings.entity_types?.[entityType.gid]?.name}/>
            </div>
            <Divider/>
            <DistributionSelection elementType={'entity_types'} element={entityType} attr={'entities_per_arrival'} dist={entityType.entities_per_arrival.dist} attrLabel={"Entities per Arrival"} distUpdater={updateEntityDistribution} warningUpdater={updateEntityTypeWarnings} configWarnings={configWarnings}/>
            <Divider/>
            <DistributionSelection elementType={'entity_types'} element={entityType} attr={'interarrival'} dist={entityType.interarrival.dist} attrLabel={"Inter-Arrival Time"} distUpdater={updateEntityDistribution} warningUpdater={updateEntityTypeWarnings} configWarnings={configWarnings}/>
            <Divider/>
            <div className='flex align-items-center'>
                <Checkbox checked={entityType.capacity.is_enabled} onChange={(event) => handleCapacityState(event.checked, entityType.gid)} style={{ marginRight: '0.5rem' }}></Checkbox>
                <label style={{ marginRight: '1rem'}}>Enable Capacity:</label>
                <InputNumber value={entityType.capacity.value} onValueChange={(event) => handleCapacity(event.target.value, entityType.gid)} disabled={!entityType.capacity.is_enabled} invalid={!!configWarnings.entity_types?.[entityType.gid]?.capacity} inputStyle={{ width: '110px' }}/>
            </div>
        </div>
    )
}

export default EntityTypePanel;